import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { isValidNumber } from '../../../utils/helper.jsx';
import { templateService } from '../services/templateService';
import { getSampleSheetUrl } from '../constants/templateApi';
import {
  validateSendForm,
  hasValidationErrors,
} from '../utils/templateValidation';
import {
  getHeaderComponent,
  initBodyParams,
  initButtonUrlParams,
  getButtonsWithExamples,
  buildSendTemplatePayload,
  parseTemplateSendError,
  parseBulkUploadError,
} from '../utils/templateSendUtils';
import { validateHeaderFile, validateExcelFile } from '../utils/templateLimitUtils';
import { validateBulkExcelFile } from '../utils/bulkSheetValidation';
import { checkPhoneOptedOut } from '../services/optoutService';

export default function useTemplateSend({ template, onClose } = {}) {
  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);

  const [loading, setLoading] = useState(false);
  const [formDataToSend, setFormDataToSend] = useState({});
  const [validationError, setValidationError] = useState({});
  const [bodyParams, setBodyParams] = useState([]);
  const [buttonUrlParams, setButtonUrlParams] = useState([]);
  const [headerFile, setHeaderFile] = useState(null);
  const [useCreateImage, setUseCreateImage] = useState(false);

  const [progressBar, setProgressBar] = useState(0);
  const [tempDataFile, setTempDataFile] = useState(null);
  const [isValidFile, setIsValidFile] = useState(true);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [isClearQueueLoading, setIsClearQueueLoading] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [queueCount, setQueueCount] = useState(0);
  const [sendError, setSendError] = useState('');
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [queueStatus, setQueueStatus] = useState(null);
  const [isRetryLoading, setIsRetryLoading] = useState(false);

  const headerComponent = getHeaderComponent(template);
  const buttonsWithExamples = getButtonsWithExamples(template);

  const resetSendFields = useCallback(() => {
    setBodyParams(initBodyParams(template));
    setButtonUrlParams(initButtonUrlParams(template));
    setFormDataToSend((prev) => ({ ...prev, phone_number: '' }));
    setHeaderFile(null);
    setUseCreateImage(false);
    setValidationError({});
  }, [template]);

  useEffect(() => {
    setBodyParams(initBodyParams(template));
    setButtonUrlParams(initButtonUrlParams(template));
  }, [template]);

  const handlePhoneNumberChange = useCallback((value) => {
    if (isValidNumber(value)) {
      setFormDataToSend((prev) => ({ ...prev, phone_number: value }));
    }
    if (!value) {
      setFormDataToSend((prev) => ({ ...prev, phone_number: '' }));
    }
    setValidationError((prev) => {
      if (!prev.phone_number) return prev;
      const next = { ...prev };
      delete next.phone_number;
      return next;
    });
  }, []);

  const getUserPhoneNumber = useCallback(() => {
    const mobile = loggedInUser?.mobile?.toString()?.replace(/\D/g, '') ?? '';
    if (!mobile) return '';
    if (mobile.length === 10) return `91${mobile}`;
    return mobile;
  }, [loggedInUser?.mobile]);

  const fillMyPhoneNumber = useCallback(() => {
    const phone = getUserPhoneNumber();
    if (!phone) {
      showSnackBar('Your profile does not have a mobile number', 'warning');
      return;
    }
    handlePhoneNumberChange(phone);
  }, [getUserPhoneNumber, handlePhoneNumberChange, showSnackBar]);

  const handleChangeParams = useCallback((value, index) => {
    setBodyParams((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], text: value };
      return arr;
    });
    setValidationError((prev) => {
      if (!prev[`bodyParams_${index}`]) return prev;
      const next = { ...prev };
      delete next[`bodyParams_${index}`];
      return next;
    });
  }, []);

  const handleButtonUrlChange = useCallback((index, value) => {
    setButtonUrlParams((prev) => {
      const arr = [...prev];
      arr[index] = value;
      return arr;
    });
  }, []);

  const onSelectHeaderFile = useCallback(
    (e) => {
      const selected = e.target.files?.[0];
      if (!selected) return;
      const validation = validateHeaderFile(selected);
      if (!validation.valid) {
        showSnackBar(validation.message, 'error');
        return;
      }
      setHeaderFile(selected);
      setUseCreateImage(false);
    },
    [showSnackBar]
  );

  const onHeaderFileRemove = useCallback(() => setHeaderFile(null), []);

  const handleUseCreateImageChange = useCallback((checked) => {
    setUseCreateImage(checked);
    if (checked) setHeaderFile(null);
  }, []);

  const sendSingle = useCallback(async () => {
    const errors = validateSendForm({
      phoneNumber: formDataToSend?.phone_number ?? '',
      bodyParams,
      buttonUrlParams,
      buttonsWithExamples,
      headerComponent,
      headerFile,
      useCreateImage,
    });
    setValidationError(errors);
    if (hasValidationErrors(errors)) return { success: false };

    if (await checkPhoneOptedOut(formDataToSend?.phone_number)) {
      setSendError('This contact has opted out of WhatsApp messages.');
      showSnackBar('Cannot send template: contact has opted out of WhatsApp messages', 'warning');
      return { success: false };
    }

    setSendError('');
    setLoading(true);
    try {
      let headerFileUrl = null;
      let headerFileName = '';

      if (
        headerComponent &&
        headerComponent.format !== 'TEXT' &&
        headerFile &&
        !useCreateImage
      ) {
        const headerFileFormData = new FormData();
        headerFileFormData.append('file', headerFile);
        const fileResponse = await templateService.uploadHeaderMedia(
          headerFileFormData,
          template?.bot_number ?? ''
        );
        if (fileResponse?.status === 200) {
          headerFileUrl = fileResponse?.data?.data?.s3_object?.url ?? '';
          headerFileName =
            fileResponse?.data?.data?.s3_object?.filename
            ?? fileResponse?.data?.data?.filename
            ?? '';
          if (!headerFileUrl) {
            showSnackBar('Header file URL was not returned. Please retry upload.', 'error');
            return { success: false };
          }
        } else {
          showSnackBar('Error when uploading the header file', 'error');
          return { success: false };
        }
      }

      const payload = buildSendTemplatePayload({
        formDataToSend,
        template,
        bodyParams,
        headerComponent,
        headerFileUrl,
        headerFileName,
        useCreateImage,
        buttonUrlParams,
      });
console.log('Sending template with payload:', payload);
      const res = await templateService.sendTemplate(payload, template?.bot_number ?? '');
      if (res?.status === 200) {
        showSnackBar('Template sent successfully', 'success');
        setSendError('');
        resetSendFields();
        return { success: true };
      }

      const errorMessage = parseTemplateSendError(res);
      setSendError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } catch (error) {
      const errorMessage = parseTemplateSendError(error?.response ?? error);
      setSendError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [
    bodyParams,
    buttonUrlParams,
    buttonsWithExamples,
    formDataToSend,
    headerComponent,
    headerFile,
    resetSendFields,
    showSnackBar,
    template,
    useCreateImage,
  ]);

  const onBulkFileSelect = useCallback(
    async (e) => {
      const selected = e.target.files?.[0];
      if (!selected) return;

      setUserCount(0);
      setQueueCount(0);
      setProgressBar(0);

      const fileValidation = validateExcelFile(selected);
      if (!fileValidation.valid) {
        showSnackBar(fileValidation.message, 'error');
        setBulkUploadError(fileValidation.message);
        e.target.value = '';
        setTempDataFile(null);
        return;
      }

      const sheetValidation = await validateBulkExcelFile(selected, template);
      if (!sheetValidation.valid) {
        showSnackBar('Invalid data in Excel file', 'error');
        setBulkUploadError(sheetValidation.message);
        e.target.value = '';
        setTempDataFile(null);
        return;
      }

      setBulkUploadError('');
      setTempDataFile(selected);
    },
    [showSnackBar, template]
  );

  const resetBulkState = useCallback(() => {
    setTempDataFile(null);
    setProgressBar(0);
    setBulkUploadError('');
    setUserCount(0);
    setQueueCount(0);
    setIsValidFile(true);
  }, []);

  const onBulkFileRemove = useCallback(() => {
    setTempDataFile(null);
    setProgressBar(0);
    setBulkUploadError('');
  }, []);

  useEffect(() => {
    setUserCount(0);
    setQueueCount(0);
    setQueueStatus(null);
    if (!template?.wa_Id) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await templateService.getBulkQueue(template.wa_Id);
        if (cancelled || res?.status !== 200) return;
        const count = Array.isArray(res?.data?.data) ? res.data.data.length : 0;
        if (count > 0) {
          setUserCount(count);
          setQueueCount(count);
        }
        const statusRes = await templateService.getBulkQueueStatus(template.wa_Id);
        if (!cancelled && statusRes?.status === 200) {
          setQueueStatus(statusRes?.data?.data ?? null);
        }
      } catch {
        // ignore fetch errors; queue section stays hidden
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [template?.wa_Id]);

  const onUploadProgress = useCallback((progressEvent) => {
    const { loaded, total } = progressEvent;
    setProgressBar(Math.floor((loaded * 100) / total));
  }, []);

  const uploadBulkFile = useCallback(async () => {
    if (!tempDataFile) return { success: false };
    setIsUploadLoading(true);
    setUserCount(0);
    setQueueCount(0);
    setBulkUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', tempDataFile);
      const res = await templateService.uploadRecipients(
        formData,
        {
          bot_number: template?.bot_number,
          wa_Id: template?.wa_Id,
          scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        },
        onUploadProgress
      );

      if (res?.status === 200) {
        setUserCount(res?.data?.data?.updated_count || 0);
        setQueueCount(Number(res?.data?.data?.queue_count) || 0);
        const statusRes = await templateService.getBulkQueueStatus(template?.wa_Id);
        if (statusRes?.status === 200) {
          setQueueStatus(statusRes?.data?.data ?? null);
        }
        setIsValidFile(true);
        setBulkUploadError('');
        onBulkFileRemove();
        showSnackBar('File Uploaded Successfully', 'success');
        return { success: true };
      }

      setIsValidFile(false);
      const errorMessage = parseBulkUploadError(res?.response ?? res);
      setBulkUploadError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } catch (error) {
      setIsValidFile(false);
      const errorMessage = parseBulkUploadError(error?.response ?? error);
      setBulkUploadError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } finally {
      setIsUploadLoading(false);
    }
  }, [onBulkFileRemove, onUploadProgress, scheduledAt, showSnackBar, tempDataFile, template]);

  const retryFailedBulk = useCallback(async () => {
    if (!template?.wa_Id) return { success: false };
    setIsRetryLoading(true);
    try {
      const res = await templateService.retryFailedBulkQueue(template.wa_Id);
      if (res?.status === 200) {
        const statusRes = await templateService.getBulkQueueStatus(template.wa_Id);
        if (statusRes?.status === 200) {
          setQueueStatus(statusRes?.data?.data ?? null);
        }
        showSnackBar('Failed items queued for retry', 'success');
        return { success: true };
      }
      showSnackBar('Failed to retry bulk send', 'error');
      return { success: false };
    } finally {
      setIsRetryLoading(false);
    }
  }, [showSnackBar, template?.wa_Id]);

  const sendBulkCron = useCallback(async () => {
    if (!template?.wa_Id) {
      showSnackBar('Template id is missing', 'warning');
      return { success: false };
    }

    setIsSendLoading(true);
    try {
      const res = await templateService.sendTemplateCron(template.wa_Id);
      if (res?.status === 200) {
        resetBulkState();
        showSnackBar('Template Send Successfully', 'success');
        return { success: true };
      }
      showSnackBar('Error occurred while sending templates. Please try again!', 'error');
      return { success: false };
    } catch (error) {
      showSnackBar(error?.toString?.() || 'Failed to send templates', 'error');
      return { success: false };
    } finally {
      setIsSendLoading(false);
    }
  }, [resetBulkState, showSnackBar, template?.wa_Id]);

  const clearBulkQueue = useCallback(async () => {
    if (!template?.wa_Id) {
      showSnackBar('Template id is missing', 'warning');
      return { success: false };
    }

    setIsClearQueueLoading(true);
    try {
      const res = await templateService.clearBulkQueue(template.wa_Id);
      if (res?.status === 200) {
        resetBulkState();
        showSnackBar('Bulk queue cleared successfully', 'success');
        return { success: true };
      }
      showSnackBar('Failed to clear bulk queue', 'error');
      return { success: false };
    } catch (error) {
      showSnackBar(error?.toString?.() || 'Failed to clear bulk queue', 'error');
      return { success: false };
    } finally {
      setIsClearQueueLoading(false);
    }
  }, [resetBulkState, showSnackBar, template?.wa_Id]);

  const downloadSampleFile = useCallback(() => {
    if (!template?.wa_Id) {
      showSnackBar('Template id is missing', 'warning');
      return;
    }
    window.location.href = getSampleSheetUrl(template.wa_Id);
  }, [showSnackBar, template?.wa_Id]);

  return {
    loading,
    formDataToSend,
    validationError,
    bodyParams,
    buttonUrlParams,
    headerFile,
    setHeaderFile,
    useCreateImage,
    headerComponent,
    buttonsWithExamples,
    progressBar,
    tempDataFile,
    setTempDataFile,
    isValidFile,
    isUploadLoading,
    isSendLoading,
    isClearQueueLoading,
    userCount,
    queueCount,
    sendError,
    bulkUploadError,
    scheduledAt,
    setScheduledAt,
    queueStatus,
    isRetryLoading,
    retryFailedBulk,
    handlePhoneNumberChange,
    handleChangeParams,
    handleButtonUrlChange,
    onSelectHeaderFile,
    onHeaderFileRemove,
    handleUseCreateImageChange,
    fillMyPhoneNumber,
    getUserPhoneNumber,
    sendSingle,
    onBulkFileSelect,
    onBulkFileRemove,
    uploadBulkFile,
    sendBulkCron,
    clearBulkQueue,
    downloadSampleFile,
  };
}
